import { ActiveUser } from "../../model/core/ActiveUser";
import { BaseModelPrivatization } from "./BaseModelPrivatization";
import { StaticDataPrivatizationType } from "./enum/StaticDataPrivatizationType";
import { Injectable } from "@angular/core";
import { Subject, Observable } from "rxjs";
import { SystemConfigPromiseService } from "shared/auth-services";
import * as CubiqTS from "shared/cubiq-model-multi-asset";

const NON_PRIVATE_CLIENT_FLAGS = new Set([
  // NOT NEEDED USE INFO ON ORDERDATA OR DEAL
  // 'straightThroughProcessingFg',
  // 'reverseEngineerTPFg',
  // 'reverseEngineerSLFg',
  // 'autoFillEnabled',
  // 'allowPartialFillFg'
]);
import { LoggerService } from "@bfx/logger";
const logger = LoggerService.for(__dirname);

@Injectable()
export class ClientModelPrivatization extends BaseModelPrivatization<
  string,
  string,
  CubiqTS.Client
> {
  private nextClientContactId = -40000; // upto -50000. +
  private clientContacts = new Map<string, CubiqTS.ClientContactImpl>();
  private clientContactsClientSegmentSubject: Subject<{
    key: CubiqTS.Long;
    value: CubiqTS.ClientContactImpl;
  }> = new Subject<{ key: CubiqTS.Long; value: CubiqTS.ClientContactImpl }>();

  getBranchPrivateLabel(branchCd: string): string {
    return `PRIVATE - ${branchCd}`;
  }
  getPrivateLabel(): string {
    return this.privateMask;
  }
  getRestrictedLabel(): string {
    return "Restricted";
  }
  getPrefixPrivate(): string {
    return this.prefix + "_private";
  }
  getPrefixRestricted(): string {
    return this.prefix + "_restricted";
  }

  private privateMask = "-PRIVATE-";
  private prefix = "__bfx_privatization";

  private readyResolve;
  private readyReject;

  public readonly ready = new Promise<void>((res, rej) => {
    this.readyResolve = res;
    this.readyReject = rej;
  });

  public constructor(private sysConfigService: SystemConfigPromiseService) {
    super();

    Promise.all([this.sysConfigService.isPrimed()]).then(() => {
      const systemPrivateMask = this.sysConfigService.hardGetSystemConfig("PRIVATE_MASK");

      if (typeof systemPrivateMask === "string" && systemPrivateMask.length > 0) {
        this.privateMask = this.sysConfigService.hardGetSystemConfig("PRIVATE_MASK");
      }

      this.readyResolve();
    });
  }

  private applyClientPermutation(client: CubiqTS.ClientImpl, cp: number): CubiqTS.ClientImpl {
    if (!(client instanceof CubiqTS.ClientImpl)) {
      throw new Error("invalid client");
    }

    if (cp < 0 || cp >= Math.pow(2, NON_PRIVATE_CLIENT_FLAGS.size)) {
      throw new Error("invalid client permutations");
    }

    for (let flag of NON_PRIVATE_CLIENT_FLAGS) {
      client[flag] = cp & 1;
      cp = cp >> 1;
    }

    return client;
  }

  public calculateClientPermutation(client: CubiqTS.ClientImpl): number {
    let cp = 0;

    if (!(client instanceof CubiqTS.ClientImpl)) {
      throw new Error("invalid client");
    }

    for (let flag of NON_PRIVATE_CLIENT_FLAGS) {
      cp = cp << 1;
      cp = cp | client[flag];
    }

    return cp;
  }

  public asModelPrivatized(label: string, key: string, id: string): CubiqTS.ClientImpl {
    let client = new CubiqTS.ClientImpl();
    let clientContact = new CubiqTS.ClientContactImpl();
    client.clientId = label;
    client.clientName = label;
    client.salesGroupId = new CubiqTS.Long(-1);

    if (this.nextClientContactId == Number.MIN_SAFE_INTEGER) {
      logger.out.warn("Too many private ClientContactImpl required");
      return client;
    }

    clientContact.id = CubiqTS.Long.fromInt(this.nextClientContactId);
    clientContact.contactNameTxt = label;
    clientContact.deskTxt = label;
    clientContact.emailTxt = label;
    clientContact.mobileTxt = label;
    clientContact.smsTxt = label;
    clientContact.clientCd = key;
    this.nextClientContactId--;

    clientContact.extendedInfo.set("__bfx_privatization_key", clientContact.id.toString());
    this.clientContacts.set(key, clientContact);

    client.extendedInfo.set("__bfx_privatization_key", key);
    return client;
  }

  public asModelRestrictedClientSegment(
    clientSegment: string,
    restrictedKey: string,
    restrictedValue: CubiqTS.ClientImpl,
  ): { key: string; value: CubiqTS.ClientImpl } {
    let restrictedClientSegment = super.asModelRestrictedClientSegment(
      clientSegment,
      restrictedKey,
      restrictedValue,
    );

    if (restrictedClientSegment == null) {
      return null;
    }

    restrictedClientSegment.key = `${restrictedKey}_${clientSegment}`;
    restrictedClientSegment.value.clientId = clientSegment;
    restrictedClientSegment.value.clientName = clientSegment;
    restrictedClientSegment.value.extendedInfo.set(
      "__bfx_privatization_key",
      restrictedClientSegment.key,
    );
    return restrictedClientSegment;
  }

  public asModelRestrictedIdName(
    id: string,
    name: string,
    restrictedKey: string,
    restrictedValue: CubiqTS.ClientImpl,
  ): { key: string; value: CubiqTS.ClientImpl } {
    let restrictedIdName = super.asModelRestrictedIdName(id, name, restrictedKey, restrictedValue);

    if (restrictedIdName == null) {
      return null;
    }

    restrictedIdName.key = `id:${id}_name:${name}`;
    restrictedIdName.value.clientId = `${restrictedIdName.value.clientId}_id:${id}_name:${name}`;
    restrictedIdName.value.clientId = id;
    restrictedIdName.value.clientName = name;
    restrictedIdName.value.extendedInfo.set("__bfx_privatization_key", restrictedIdName.key);

    return restrictedIdName;
  }

  public asClientContactRestrictedClientSegment(
    clientSegment: string,
    restrictedKey: string,
    restrictedValue: CubiqTS.ClientImpl,
    restrictedClientContactsKey: CubiqTS.Long,
    restrictedClientContactsValue: CubiqTS.ClientContactImpl,
  ): { key: CubiqTS.Long; value: CubiqTS.ClientContactImpl } {
    let clientSegmentHash = this.hashClientSegment(clientSegment);
    let clientContact = new CubiqTS.ClientContactImpl();

    if (clientSegmentHash < 0) {
      // represent negativity
      clientContact.id = CubiqTS.Long.fromString(
        `${restrictedKey.toString()}1${Math.abs(clientSegmentHash)}`,
      );
    } else {
      let b = new CubiqTS.Long(Math.abs(clientSegmentHash), 1);
      clientContact.id = CubiqTS.Long.fromString(
        `${restrictedKey.toString()}0${Math.abs(clientSegmentHash)}`,
      );
    }
    clientContact.contactNameTxt = clientSegment;
    clientContact.deskTxt = clientSegment;
    clientContact.emailTxt = clientSegment;
    clientContact.mobileTxt = clientSegment;
    clientContact.smsTxt = clientSegment;
    clientContact.clientCd = `${restrictedKey}_${clientSegment}`;

    this.clientContacts.set(`${restrictedKey}_${clientSegment}`, clientContact);

    return {
      key: clientContact.id,
      value: clientContact,
    };
  }

  public addRestrictedClientSegment(
    clientSegment: string,
    restrictedKey: string,
    restrictedValue: CubiqTS.ClientImpl,
    restrictedClientContactsKey?: CubiqTS.Long,
    restrictedClientContactsValue?: CubiqTS.ClientContactImpl,
  ): string {
    if (restrictedClientContactsKey == null || restrictedClientContactsValue == null) {
      return super.addRestrictedClientSegment(clientSegment, restrictedKey, restrictedValue);
    }

    let clientContactRestrictedClientSegment = this.asClientContactRestrictedClientSegment(
      clientSegment,
      restrictedKey,
      restrictedValue,
      restrictedClientContactsKey,
      restrictedClientContactsValue,
    );

    StaticDataPrivatizationType.setPrivatization(
      clientContactRestrictedClientSegment.value,
      StaticDataPrivatizationType.RestrictedClientSegment,
    );

    this.clientContactsClientSegmentSubject.next(clientContactRestrictedClientSegment);

    return super.addRestrictedClientSegment(clientSegment, restrictedKey, restrictedValue);
  }

  public getPrivate(): Map<string, CubiqTS.ClientImpl> {
    let clients = new Map<string, CubiqTS.ClientImpl>();
    const size = Math.pow(2, NON_PRIVATE_CLIENT_FLAGS.size);

    for (let i = 0; i < size; i++) {
      let key = `${this.prefix}_private_${i}`;
      let client = this.asModelPrivatized(this.getPrivateLabel(), key, key);
      StaticDataPrivatizationType.setPrivatization(client, StaticDataPrivatizationType.Private);
      this.applyClientPermutation(client, i);
      clients.set(key, client);
    }

    return clients;
  }

  public getPrivateKey(client: CubiqTS.ClientImpl | null): string {
    return this.getPrivateId(client);
  }

  public getPrivateId(client: CubiqTS.ClientImpl | null): string {
    if (!client) return `${this.prefix}_private_0`;

    return `${this.prefix}_private_${this.calculateClientPermutation(client)}`;
  }

  public getBranchPrivate(branchCd: string): Map<string, CubiqTS.ClientImpl> {
    let clients = new Map<string, CubiqTS.ClientImpl>();
    const size = Math.pow(2, NON_PRIVATE_CLIENT_FLAGS.size);

    for (let i = 0; i < size; i++) {
      let key = `${this.prefix}_private_${branchCd}_${i}`;
      let client = this.asModelPrivatized(this.getBranchPrivateLabel(branchCd), key, key);
      StaticDataPrivatizationType.setPrivatization(
        client,
        StaticDataPrivatizationType.BranchPrivate,
      );
      this.applyClientPermutation(client, i);
      clients.set(key, client);
    }

    return clients;
  }

  public getBranchPrivateKey(branchCd: string, client: CubiqTS.ClientImpl | null): string {
    return this.getBranchPrivateId(branchCd, client);
  }

  public getBranchPrivateId(branchCd: string, client: CubiqTS.ClientImpl | null): string {
    if (!client) return `${this.prefix}_private_${branchCd}_0`;

    return `${this.prefix}_private_${branchCd}_${this.calculateClientPermutation(client)}`;
  }

  public getRestricted(): Map<string, CubiqTS.ClientImpl> {
    let clients = new Map<string, CubiqTS.ClientImpl>();
    const size = Math.pow(2, NON_PRIVATE_CLIENT_FLAGS.size);

    for (let i = 0; i < size; i++) {
      let key = `${this.prefix}_restricted_${i}`;
      let client = this.asModelPrivatized(this.getRestrictedLabel(), key, key);
      StaticDataPrivatizationType.setPrivatization(client, StaticDataPrivatizationType.Restricted);
      this.applyClientPermutation(client, i);
      clients.set(key, client);
    }

    return clients;
  }

  public getRestrictedKey(client: CubiqTS.ClientImpl | null = null): string {
    return this.getRestrictedId(client);
  }

  public getRestrictedId(client: CubiqTS.ClientImpl | null = null): string {
    if (!client) return `${this.prefix}_restricted_0`;

    return `${this.prefix}_restricted_${this.calculateClientPermutation(client)}`;
  }

  public getClientContacts(): IterableIterator<CubiqTS.ClientContactImpl> {
    return this.clientContacts.values();
  }

  public getClientContactIdForPrivatizationKey(key: string): CubiqTS.Long | null {
    if (this.clientContacts.has(key)) {
      let clientContact = this.clientContacts.get(key);

      if (
        clientContact instanceof CubiqTS.ClientContactImpl &&
        CubiqTS.Long.isLong(clientContact.id)
      )
        return clientContact.id;
    }

    return null;
  }

  get clientContactsRestrictedClientSegment$(): Observable<{
    key: CubiqTS.Long;
    value: CubiqTS.ClientContactImpl;
  }> {
    return this.clientContactsClientSegmentSubject.asObservable();
  }
}
